(() => {
  if (customElements.get('product-detail')) return;
  class ProductDetail extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      try {
        this.data = JSON.parse(this.querySelector('[data-pdp-data]').textContent);
        this.strings = JSON.parse(this.querySelector('[data-pdp-strings]').textContent);
      } catch (_) { return; }
      this.variantControls = Array.from(this.querySelectorAll('[data-pdp-variant]'));
      this.gallery = this.querySelector('[data-pdp-gallery]');
      this.galleryFrames = Array.from(this.querySelectorAll('[data-pdp-media]'));
      this.galleryControls = this.querySelector('[data-pdp-gallery-controls]');
      this.details = this.querySelector('[data-pdp-details]');
      this.detailButtons = Array.from(this.querySelectorAll('[data-pdp-tab]'));
      this.detailPanels = Array.from(this.querySelectorAll('[data-pdp-panel]'));
      this.abortController = new AbortController();
      const signal = this.abortController.signal;
      this.variantControls.forEach(control => control.addEventListener('change', () => this.updateVariant(true), { signal }));
      this.addEventListener('click', (event) => {
        const detailTab = event.target.closest('[data-pdp-tab]');
        if (detailTab && this.contains(detailTab)) this.selectDetailsTab(detailTab.dataset.pdpTab, false);
        const fitLink = event.target.closest('[data-pdp-fit-link]');
        if (fitLink && this.contains(fitLink)) this.selectDetailsTab('fit', false);
        const arrow = event.target.closest('[data-pdp-gallery-step]');
        if (arrow && this.contains(arrow)) this.moveGallery(Number(arrow.dataset.pdpGalleryStep));
        const thumbnail = event.target.closest('[data-pdp-media-trigger]');
        if (thumbnail && this.contains(thumbnail)) { event.preventDefault(); this.selectMedia(thumbnail.dataset.pdpMediaTrigger, true); }
        const button = event.target.closest('[data-pdp-load-media]');
        if (button && this.contains(button)) this.loadMedia(button);
      }, { signal });
      this.details?.querySelector('[role="tablist"]')?.addEventListener('keydown', (event) => {
        const buttons = this.detailButtons.filter(button => !button.hidden);
        const current = buttons.indexOf(event.target);
        if (current < 0 || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const index = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : (current + (event.key === 'ArrowRight' ? 1 : -1) + buttons.length) % buttons.length;
        this.selectDetailsTab(buttons[index].dataset.pdpTab, true);
      }, { signal });
      window.addEventListener('hashchange', () => {
        const panel = this.detailPanels.find(item => '#' + item.id === location.hash);
        if (panel) this.selectDetailsTab(panel.dataset.pdpPanel, false);
      }, { signal });
      this.gallery?.addEventListener('scroll', () => {
        if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = requestAnimationFrame(() => this.updateGalleryState());
      }, { passive: true, signal });
      this.gallery?.addEventListener('keydown', (event) => {
        if (event.target !== this.gallery || !this.galleryFrames.length) return;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault(); this.moveGallery(event.key === 'ArrowRight' ? 1 : -1);
        } else if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault(); this.selectMedia(this.galleryFrames[event.key === 'Home' ? 0 : this.galleryFrames.length - 1].dataset.pdpMedia, false);
        }
      }, { signal });
      this.querySelector('form.pdp-form')?.addEventListener('submit', (event) => {
        const variant = this.selectedVariant();
        if (!variant || !this.isPurchasable(variant)) event.preventDefault();
      }, { signal });
      this.updateVariant(false);
      this.updateGalleryState();
      if (this.gallery && 'ResizeObserver' in window) {
        this.galleryObserver = new ResizeObserver(() => {
          const active = this.galleryFrames.find(frame => frame.dataset.pdpMedia === this.activeMediaId);
          if (active && this.gallery.clientWidth) this.gallery.scrollTo({ left: active.offsetLeft, behavior: 'auto' });
          this.updateGalleryState();
        });
        this.galleryObserver.observe(this.gallery);
      }
    }
    disconnectedCallback() {
      this.abortController?.abort();
      this.galleryObserver?.disconnect();
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      this.initialized = false;
    }
    selectedVariant() {
      const control = this.querySelector('[data-pdp-variant]:checked') || this.variantControls[0];
      return this.data.variants.find((variant) => String(variant.id) === control?.value);
    }
    isComingSoon(variant) { return ['coming_soon', 'preproduction'].includes(this.data.productLifecycle) || ['coming_soon', 'preproduction'].includes(variant.lifecycle); }
    isPurchasable(variant) { return variant.available && variant.purchaseEnabled && this.data.productPurchaseEnabled && this.data.productLifecycle !== 'inactive' && variant.lifecycle !== 'inactive' && !this.isComingSoon(variant); }
    updateVariant(updateUrl) {
      const variant = this.selectedVariant();
      if (!variant) return;
      const canPurchase = this.isPurchasable(variant);
      const comingSoon = this.isComingSoon(variant);
      const disabledByMerchant = !variant.purchaseEnabled || !this.data.productPurchaseEnabled || variant.lifecycle === 'inactive' || this.data.productLifecycle === 'inactive';
      const state = comingSoon ? 'coming_soon' : disabledByMerchant ? 'order_unavailable' : variant.available ? 'available' : 'sold_out';
      this.querySelector('[data-pdp-price]').innerHTML = variant.price;
      const compare = this.querySelector('[data-pdp-compare]');
      compare.innerHTML = variant.comparePrice;
      compare.hidden = !(variant.compareAmount > variant.priceAmount);
      this.querySelector('[data-pdp-price-wrap]').hidden = comingSoon;
      this.querySelector('[data-pdp-status]').textContent = this.strings[state];
      const button = this.querySelector('[data-pdp-submit]');
      button.disabled = !canPurchase;
      button.textContent = this.strings[canPurchase ? 'add_to_cart' : state];
      this.querySelector('[data-pdp-sku]').textContent = variant.sku;
      this.querySelector('[data-pdp-sku-row]').hidden = !variant.sku;
      for (const [key, value] of Object.entries(variant.fields)) {
        this.querySelectorAll(`[data-pdp-field="${key}"]`).forEach((element) => { element.textContent = value; });
        this.querySelectorAll(`[data-pdp-field-row="${key}"], [data-pdp-field-section="${key}"]`).forEach((element) => { element.hidden = !value; });
      }
      this.querySelectorAll('[data-pdp-compare-field]').forEach(cell => {
        const value = variant.fields[cell.dataset.pdpCompareField];
        cell.textContent = value || this.strings.not_listed;
        cell.dataset.pdpComparisonValue = String(Boolean(value));
      });
      this.querySelectorAll('[data-pdp-comparison-row]').forEach(row => {
        row.hidden = !row.querySelector('[data-pdp-comparison-value="true"]');
      });
      const hasSpecs = ['electrical_rating', 'speed_settings', 'airflow_notes', 'noise_notes'].some((key) => Boolean(variant.fields[key]));
      this.querySelector('[data-pdp-spec-section]').hidden = !hasSpecs;
      let hasResources = false;
      for (const [key, value] of Object.entries(variant.resources)) {
        const link = this.querySelector(`[data-pdp-resource="${key}"]`);
        let safeUrl = false;
        try { const url = new URL(value, location.origin); safeUrl = Boolean(value) && ['http:', 'https:'].includes(url.protocol); } catch (_) { safeUrl = false; }
        link.hidden = !safeUrl;
        if (safeUrl) { link.href = value; hasResources = true; } else link.removeAttribute('href');
      }
      this.querySelector('[data-pdp-resource-section]').hidden = !hasResources;
      const fitKeys = ['overall_dimensions', 'height_range', 'air_center_height', 'base_dimensions', 'wall_projection', 'net_weight', 'setup_notes'];
      const hasFit = fitKeys.some(key => Boolean(variant.fields[key]));
      const fitPanel = this.querySelector('[data-pdp-panel="fit"]');
      fitPanel?.classList.toggle('pdp-fit--guidance-only', !hasFit);
      const fitFacts = this.querySelector('[data-pdp-fit-facts]');
      if (fitFacts) fitFacts.hidden = !hasFit;
      this.detailAvailability = { description: this.dataset.pdpDescription === 'true', specifications: hasSpecs, fit: hasFit || this.dataset.pdpFitGuidance === 'true', box: Boolean(variant.fields.box_contents), resources: hasResources };
      const fitLink = this.querySelector('[data-pdp-fit-link]');
      if (fitLink) fitLink.hidden = !this.detailAvailability.fit;
      this.syncDetails();
      const quantity = this.querySelector('[data-pdp-quantity]');
      quantity.min = variant.quantity.min || 1;
      quantity.step = variant.quantity.increment || 1;
      if (variant.quantity.max) quantity.max = variant.quantity.max; else quantity.removeAttribute('max');
      const minimum = Number(quantity.min);
      const step = Number(quantity.step);
      let value = Math.max(minimum, Number(quantity.value) || minimum);
      value = minimum + Math.ceil((value - minimum) / step) * step;
      if (variant.quantity.max) value = Math.min(value, variant.quantity.max);
      quantity.value = value;
      quantity.disabled = !canPurchase;
      if (variant.mediaId) this.selectMedia(String(variant.mediaId), false);
      if (updateUrl) { const url = new URL(location.href); url.searchParams.set('variant', variant.id); history.replaceState({}, '', url); }
    }
    syncDetails() {
      if (!this.details || !this.detailButtons.length) return;
      const available = this.detailButtons.filter(button => this.detailAvailability[button.dataset.pdpTab]);
      const hashPanel = this.detailPanels.find(panel => '#' + panel.id === location.hash);
      if (!this.selectedDetailKey) this.selectedDetailKey = hashPanel?.dataset.pdpPanel;
      if (!this.detailAvailability[this.selectedDetailKey]) this.selectedDetailKey = available[0]?.dataset.pdpTab;
      const enhanced = available.length > 1;
      this.details.hidden = !available.length;
      this.details.classList.toggle('is-enhanced', enhanced);
      this.details.querySelector('[role="tablist"]').hidden = !enhanced;
      this.detailButtons.forEach(button => {
        const active = button.dataset.pdpTab === this.selectedDetailKey;
        button.hidden = !this.detailAvailability[button.dataset.pdpTab];
        button.setAttribute('aria-selected', String(active));
        button.tabIndex = active ? 0 : -1;
      });
      this.detailPanels.forEach(panel => {
        const key = panel.dataset.pdpPanel;
        panel.hidden = !this.detailAvailability[key] || (enhanced && key !== this.selectedDetailKey);
        if (enhanced) {
          panel.setAttribute('role', 'tabpanel');
          panel.setAttribute('aria-labelledby', this.detailButtons.find(button => button.dataset.pdpTab === key).id);
          panel.tabIndex = 0;
        } else {
          panel.removeAttribute('role'); panel.removeAttribute('aria-labelledby'); panel.removeAttribute('tabindex');
        }
      });
    }
    selectDetailsTab(key, focus) {
      if (!this.detailAvailability?.[key]) return;
      this.selectedDetailKey = key;
      this.syncDetails();
      const button = this.detailButtons.find(item => item.dataset.pdpTab === key);
      if (focus) button?.focus({ preventScroll: true });
      if (button && !button.parentElement.hidden) {
        const bounds = button.getBoundingClientRect(), rail = button.parentElement.getBoundingClientRect();
        if (bounds.left < rail.left) button.parentElement.scrollBy({ left: bounds.left - rail.left, behavior: 'auto' });
        else if (bounds.right > rail.right) button.parentElement.scrollBy({ left: bounds.right - rail.right, behavior: 'auto' });
      }
    }
    selectMedia(id, focus) {
      const frame = this.querySelector(`[data-pdp-media="${id}"]`);
      if (!frame || !this.gallery) return;
      this.pauseMedia();
      this.gallery.scrollTo({ left: frame.offsetLeft, behavior: 'auto' });
      this.setGalleryState(frame);
      if (focus) frame.querySelector('[data-pdp-load-media]')?.focus({ preventScroll: true });
    }
    updateGalleryState() {
      if (!this.gallery) return;
      const frames = this.galleryFrames;
      if (!frames.length) return;
      const frame = frames.reduce((nearest, item) => Math.abs(item.offsetLeft - this.gallery.scrollLeft) < Math.abs(nearest.offsetLeft - this.gallery.scrollLeft) ? item : nearest, frames[0]);
      this.setGalleryState(frame);
      this.pauseMedia(frame);
    }
    moveGallery(step) {
      const current = this.galleryFrames.findIndex(frame => frame.dataset.pdpMedia === this.activeMediaId);
      const next = Math.max(0, Math.min(this.galleryFrames.length - 1, current + step));
      const frame = this.galleryFrames[next];
      if (frame && next !== current) this.selectMedia(frame.dataset.pdpMedia, false);
    }
    setGalleryState(frame) {
      this.activeMediaId = frame.dataset.pdpMedia;
      const index = this.galleryFrames.indexOf(frame);
      this.querySelectorAll('[data-pdp-media-trigger]').forEach(trigger => {
        if (trigger.dataset.pdpMediaTrigger === this.activeMediaId) trigger.setAttribute('aria-current', 'true'); else trigger.removeAttribute('aria-current');
      });
      if (this.galleryControls) {
        this.galleryControls.hidden = this.galleryFrames.length < 2;
        this.galleryControls.querySelector('[data-pdp-gallery-step="-1"]').disabled = index === 0;
        this.galleryControls.querySelector('[data-pdp-gallery-step="1"]').disabled = index === this.galleryFrames.length - 1;
      }
      const thumb = this.querySelector('[data-pdp-media-trigger][aria-current="true"]');
      const rail = thumb?.parentElement;
      if (thumb && rail) {
        const bounds = thumb.getBoundingClientRect();
        const viewport = rail.getBoundingClientRect();
        if (bounds.left < viewport.left + 3) rail.scrollBy({ left: bounds.left - viewport.left - 3, behavior: 'auto' });
        else if (bounds.right > viewport.right - 3) rail.scrollBy({ left: bounds.right - viewport.right + 3, behavior: 'auto' });
      }
    }
    loadMedia(button) {
      const frame = button.closest('[data-pdp-media]');
      const template = frame.querySelector('[data-pdp-media-template]');
      if (!template) return;
      this.pauseMedia();
      frame.appendChild(template.content.cloneNode(true));
      button.remove(); template.remove();
      const video = frame.querySelector('video');
      const player = video || frame.querySelector('iframe, model-viewer');
      player?.setAttribute('tabindex', '0'); player?.focus({ preventScroll: true });
      if (video) video.play().catch(() => {});
    }
    pauseMedia(exceptFrame) {
      this.querySelectorAll('[data-pdp-media]').forEach((frame) => {
        if (frame === exceptFrame) return;
        frame.querySelectorAll('video').forEach((video) => video.pause());
        frame.querySelectorAll('model-viewer').forEach((model) => { if (model.pause) model.pause(); });
        frame.querySelectorAll('iframe').forEach((iframe) => {
          iframe.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }), '*');
          iframe.contentWindow?.postMessage(JSON.stringify({ method: 'pause' }), '*');
        });
      });
    }
  }
  customElements.define('product-detail', ProductDetail);
})();
